import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, IconButton, OutlinedInput, Typography } from "@mui/material";
import Modal from "@mui/material/Modal";
import React, { useEffect, useState } from "react";
import { useAccessibility } from "../../../../../../../Store/contexts/AccessibilityContext";
import {
  sxButtons,
  sxCloseIconButton,
  sxModal,
  sxInput,
  sxLabel,
  sxValidationMessage,
  sxOutlinedButton,
} from "./sxProps";
import DefaultTextBold from "../../../../../../../components/Accessibility/DefaultTextBold";
import DefaultButton from "../../../../../../../components/Accessibility/DefaultButton";
import {
  cellphoneMask,
  phoneMask,
} from "../../../../../../../utils/PhoneRegex";
import { useFormik } from "formik";
import * as Yup from "yup";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";

interface UserData {
  cel: string;
  tel: string;
  email: string;
}

interface Props {
  showModal: boolean;
  handleCancelarClick: () => void;
  handleSalvarClick: (data: UserData) => void;
  userData: UserData;
}

const ContatoModal: React.FC<Props> = ({
  showModal,
  handleCancelarClick,
  handleSalvarClick,
  userData,
}) => {
  const accessibility = useAccessibility();

  const formik = useFormik({
    initialValues: {
      tel: cellphoneMask(userData.tel),
      cel: cellphoneMask(userData.cel),
      email: userData.email,
    },
    validationSchema: Yup.object({
      cel: Yup.string()
        .required("Celular é obrigatório")
        .matches(/^\(\d{2}\)\s\d{5}-\d{4}$/, "Celular inválido ou incorreto"),
      tel: Yup.string()
        .required("Telefone é obrigatório")
        .matches(/^\(\d{2}\)\s\d{4}-\d{4}$/, "Telefone inválido ou incorreto"),
      email: Yup.string()
        .email("E-mail inválido ou incorreto")
        .required("E-mail é obrigatório"),
    }),
    onSubmit: (values) => {
      handleSalvarClick(values);
      formik.resetForm();
    },
  });

  useEffect(() => {
    formik.setValues({
      tel: cellphoneMask(userData.tel),
      cel: cellphoneMask(userData.cel),
      email: userData.email,
    });
  }, [showModal]);

  return (
    <div>
      <Modal open={showModal}>
        <Box
          sx={sxModal}
          style={{
            ...accessibility.textStyleAccessibility,
            ...accessibility.contrastBorderAccessibility,
          }}
        >
          <IconButton
            aria-label="fechar"
            onClick={handleCancelarClick}
            sx={sxCloseIconButton}
          >
            <FontAwesomeIcon
              icon={faTimes}
              size="1x"
              color="black"
              style={accessibility.textStyleAccessibility}
            />
          </IconButton>
          <DefaultTextBold
            marginBottom="15px"
            large
            text="Atualizar dados do contato"
            component={"h1"}
          />
          <div className="flex-col gap-20" style={{ marginBottom: "30px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Typography
                component="label"
                htmlFor="telefone"
                style={accessibility.textStyleAccessibility}
                sx={sxLabel}
              >
                Telefone
              </Typography>
              <OutlinedInput
                id="telefone"
                style={accessibility.textStyleAccessibility}
                type={"tel"}
                value={formik.values.tel}
                onChange={(e) =>
                  formik.setFieldValue("tel", phoneMask(e.target.value))
                }
                name="tel"
                aria-label="telefone"
                maxRows={1}
                inputProps={{ maxLength: 14 }}
                sx={sxInput}
              />
              {formik.touched.tel && formik.errors.tel && (
                <div className="container-validation-message">
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    size="sm"
                    color="#D84141"
                  />
                  <Typography
                    style={accessibility.textStyleAccessibility}
                    id="errorTel"
                    sx={sxValidationMessage}
                  >
                    {formik.errors.tel}
                  </Typography>
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Typography
                component="label"
                htmlFor="celular"
                style={accessibility.textStyleAccessibility}
                sx={sxLabel}
              >
                Celular
              </Typography>
              <OutlinedInput
                id="celular"
                style={accessibility.textStyleAccessibility}
                type={"tel"}
                onChange={(e) =>
                  formik.setFieldValue("cel", cellphoneMask(e.target.value))
                }
                value={formik.values.cel}
                name="cel"
                aria-label="celular"
                maxRows={1}
                inputProps={{ maxLength: 15 }}
                sx={sxInput}
              />
              {formik.touched.cel && formik.errors.cel && (
                <div className="container-validation-message">
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    size="sm"
                    color="#D84141"
                  />
                  <Typography
                    style={accessibility.textStyleAccessibility}
                    id="errorCel"
                    sx={sxValidationMessage}
                  >
                    {formik.errors.cel}
                  </Typography>
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Typography
                component="label"
                htmlFor="email"
                style={accessibility.textStyleAccessibility}
                sx={sxLabel}
              >
                E-mail
              </Typography>
              <OutlinedInput
                id="email"
                style={accessibility.textStyleAccessibility}
                type={"email"}
                onChange={formik.handleChange}
                value={formik.values.email}
                inputProps={{ maxLength: 50 }}
                name="email"
                maxRows={1}
                sx={sxInput}
              />
              {formik.touched.email && formik.errors.email && (
                <div className="container-validation-message">
                  <FontAwesomeIcon
                    icon={faCircleExclamation}
                    size="sm"
                    color="#D84141"
                  />
                  <Typography
                    style={accessibility.textStyleAccessibility}
                    id="errorEmail"
                    sx={sxValidationMessage}
                  >
                    {formik.errors.email}
                  </Typography>
                </div>
              )}
            </div>
          </div>

          <Box sx={sxButtons}>
            <DefaultButton
              variant="outlined"
              onClick={handleCancelarClick}
              sx={sxOutlinedButton}
            >
              <Typography style={{ color: "#00995D" }}>Cancelar</Typography>
            </DefaultButton>
            <DefaultButton
              text="Salvar"
              onClick={formik.submitForm}
            />
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default ContatoModal;
